import type { ApprovalMode, EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address } from "viem"
import type { Config } from "wagmi"
import { MinimalPoolStore } from "@/domains/minimal-pool"
import { addPairLiquidity, approveOwnerSpenderAllowance, waitForAllowance } from "@/domains/on-chain"

type AddLiquidityInteractionParams = {
  amount0: bigint
  amount1: bigint
  approvalMode: ApprovalMode
  chainId: number
  config: Config
  connectedAddress: Address
  deadline: number
  emitter: EventEmitter<EmitEvent>
  minimalPool: MinimalPoolStore
  routerAddress: Address
  slippage: number
  onSuccess: () => void
}

export const addLiquidityInteraction = async ({
  amount0,
  amount1,
  approvalMode,
  chainId,
  config,
  connectedAddress,
  deadline,
  emitter,
  minimalPool,
  routerAddress,
  slippage,
  onSuccess,
}: AddLiquidityInteractionParams) => {
  if (!connectedAddress) {
    emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
    return
  }
  if (!minimalPool) {
    emitter.emit("error-missing-minimal-pool", {
      title: "Missing minimal pool",
      description: "Invalid value for minimal pool, please try again.",
      variant: "error",
    })
    return
  }
  if (!minimalPool.token0) {
    emitter.emit("error-missing-token", {
      title: "Missing token",
      description: "Invalid value for token0, please try again.",
      variant: "error",
    })
    return
  }
  if (!minimalPool.token1) {
    emitter.emit("error-missing-token", {
      title: "Missing token",
      description: "Invalid value for token1, please try again.",
      variant: "error",
    })
    return
  }
  if (!amount0) {
    emitter.emit("error-missing-amount-token0", {
      title: "Missing amount token0",
      description: "Invalid value for amountToken0, please try again.",
      variant: "error",
    })
    return
  }
  if (!amount1) {
    emitter.emit("error-missing-amount-token1", {
      title: "Missing amount token1",
      description: "Invalid value for amountToken1, please try again.",
      variant: "error",
    })
    return
  }

  // --------------------------------------------------------------------------------
  // STEP ONE: Check if the user has enough balance to make LP
  if (minimalPool.token0.balance < amount0) {
    emitter.emit("error-insufficient-balance-token0", {
      title: "Insufficient balance",
      description: `You need ${minimalPool.token0.formatUnits(amount0 - minimalPool.token0.balance)} more ${minimalPool.token0.symbol} to make this transaction`,
      variant: "error",
    })
    return
  }
  if (minimalPool.token1.balance < amount1) {
    emitter.emit("error-insufficient-balance-token1", {
      title: "Insufficient balance",
      description: `You need ${minimalPool.token1.formatUnits(amount1 - minimalPool.token1.balance)} more ${minimalPool.token1.symbol} to make this transaction`,
      variant: "error",
    })
    return
  }

  // --------------------------------------------------------------------------------
  // STEP TWO: Check if the user has enough allowance to make LP

  if (minimalPool.onChainUserToken0RouterAllowance < amount0) {
    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: minimalPool.token0.address.toViemAddress(),
      spenderAddress: routerAddress,
      spenderAmount: amount0,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (!approval.success) {
      emitter.emit("error-approve-token0", {
        title: `Unable to approve ${minimalPool.token0.symbol} allowance`,
        description: approval.error || "Unknown error",
        variant: "error",
      })
      return
    }

    try {
      await waitForAllowance({
        config,
        chainId,
        contractAddress: minimalPool.token0.address.toViemAddress(),
        ownerAddress: connectedAddress,
        spenderAddress: routerAddress,
        requiredAmountWei: amount0,
        pollIntervalMs: 2000,
        timeoutMs: 120_000,
      })
      minimalPool.onChainUserToken0RouterAllowance = amount0
    } catch (e) {
      emitter.emit("error-approve-token0", {
        title: `Waiting for ${minimalPool.token0.symbol} approval`,
        description:
          (e instanceof Error ? e.message : "Allowance did not confirm in time. The network may be slow. Please try again."),
        variant: "error",
      })
      return
    }
  }

  if (minimalPool.onChainUserToken1RouterAllowance < amount1) {
    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: minimalPool.token1.address.toViemAddress(),
      spenderAddress: routerAddress,
      spenderAmount: amount1,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (!approval.success) {
      emitter.emit("error-approve-token1", {
        title: `Unable to approve ${minimalPool.token1.symbol} allowance`,
        description: approval.error || "Unknown error",
        variant: "error",
      })
      return
    }

    try {
      await waitForAllowance({
        config,
        chainId,
        contractAddress: minimalPool.token1.address.toViemAddress(),
        ownerAddress: connectedAddress,
        spenderAddress: routerAddress,
        requiredAmountWei: amount1,
        pollIntervalMs: 2000,
        timeoutMs: 120_000,
      })
      minimalPool.onChainUserToken1RouterAllowance = amount1
    } catch (e) {
      emitter.emit("error-approve-token1", {
        title: `Waiting for ${minimalPool.token1.symbol} approval`,
        description:
          (e instanceof Error ? e.message : "Allowance did not confirm in time. The network may be slow. Please try again."),
        variant: "error",
      })
      return
    }
  }

  // --------------------------------------------------------------------------------
  // STEP THREE: Make the LP
  // Convert slippage to basis points (multiply by 100 for precision)
  // e.g., 0.5% slippage = 50 basis points, keep (10000 - 50) / 10000 = 9950 / 10000 of desired amount
  const slippageBasisPoints = BigInt(Math.floor(slippage * 100))
  const slippageMultiplier = 10000n - slippageBasisPoints

  const amountADesired = amount0
  const amountBDesired = amount1
  const amountAMin = (amountADesired * slippageMultiplier) / 10000n
  const amountBMin = (amountBDesired * slippageMultiplier) / 10000n

  // This should be a tested interaction
  const addPairLiquidityResult = await addPairLiquidity({
    account: connectedAddress,
    chainId,
    config,
    minimalPool,
    routerAddress,
    amountADesired,
    amountBDesired,
    amountAMin,
    amountBMin,
    deadline,
  })

  if (addPairLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-add-liquidity", {
      title: `Created ${minimalPool.displayName} liquidity`,
      description: `Added ${minimalPool.token0.formatUnits(amount0)} ${minimalPool.token0.symbol} and ${minimalPool.token1.formatUnits(amount1)} ${minimalPool.token1.symbol} to ${minimalPool.displayName} and automatically staked in the gauge.`,
      variant: "success",
    })
  } else {
    emitter.emit("error-add-liquidity", {
      title: `Unable to create ${minimalPool.displayName} liquidity`,
      description: addPairLiquidityResult.error || "Unknown error",
      variant: "error",
    })
    return
  }

  return addPairLiquidityResult
}
